import { useState } from 'react'
import { useCourses } from '../../hooks/useApi'
import { Icon } from '@iconify/react'
import { Search, X } from 'lucide-react'
import type { CourseInfo } from '../../types/api'
import InteractiveCategoryCard from '../../components/CategoryGrid/InteractiveCategoryCard'
import '../../components/CategoryGrid/CategoryGrid.css'
import './CoursesPage.css'

const CoursesPage = () => {
  const { data, isLoading, error } = useCourses()
  const [searchQuery, setSearchQuery] = useState('')

  if (isLoading) {
    return (
      <div className="courses-page">
        <div className="courses-loading">
          <div className="courses-loading-spinner" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="courses-page">
        <div className="courses-placeholder">
          <Icon
            icon="solar:danger-triangle-linear"
            width={48}
            height={48}
            className="courses-placeholder-icon"
          />
          <h2 className="courses-placeholder-title">Ошибка</h2>
          <p className="courses-placeholder-text">
            Не удалось загрузить курсы. Попробуйте позже.
          </p>
        </div>
      </div>
    )
  }

  const courses = data?.courses || []

  if (courses.length === 0) {
    return (
      <div className="courses-page">
        <div className="courses-placeholder">
          <Icon
            icon="solar:notebook-minimalistic-linear"
            width={48}
            height={48}
            className="courses-placeholder-icon"
          />
          <h2 className="courses-placeholder-title">Скоро..</h2>
          <p className="courses-placeholder-text">
            Данный раздел находится в разработке, следите за анонсами в Secret
            Chat
          </p>
        </div>
      </div>
    )
  }

  // Фильтрация курсов по поисковому запросу
  const filterBySearch = (coursesList: CourseInfo[]) => {
    if (!searchQuery.trim()) return coursesList
    const query = searchQuery.toLowerCase()
    return coursesList.filter((c) =>
      c.base.name.toLowerCase().includes(query)
    )
  }

  const buyCourses = filterBySearch(
    courses.filter((c: CourseInfo) => c.base.grant_type === 'buy')
  )
  const subCourses = filterBySearch(
    courses.filter((c: CourseInfo) => c.base.grant_type === 'subscription')
  )

  return (
    <div className="courses-page">
      {/* Поиск */}
      <div className="courses-search">
        <div className="courses-search-container">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по курсам"
            className="courses-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="courses-search-clear"
              aria-label="Очистить поиск"
            >
              <X size={18} />
            </button>
          )}
          <div className="courses-search-icon">
            <Search size={20} />
          </div>
        </div>
      </div>

      {/* Курсы (покупка) */}
      {buyCourses.length > 0 && (
        <div className="courses-section">
          <div className="courses-section-header">
            <h2 className="courses-section-title">Курсы</h2>
          </div>
          <div className="courses-scroll">
            {buyCourses.map((course: CourseInfo) => {
              const hasTrial = (course.base.trial_lesson_count ?? 0) > 0
              return (
                <InteractiveCategoryCard
                  key={course.base.course_id}
                  item={course}
                  showingType="courses"
                  className="category-card course-card-large"
                  showLockBadge={!course.access && !hasTrial}
                  showDemoBadge={!course.access && hasTrial}
                  searchQuery={searchQuery}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Мини-курсы (подписка) */}
      {subCourses.length > 0 && (
        <div className="courses-section">
          <div className="courses-section-header">
            <h2 className="courses-section-title">Мини-курсы</h2>
          </div>
          <div className="courses-mini-grid">
            {subCourses.map((course: CourseInfo) => (
              <InteractiveCategoryCard
                key={course.base.course_id}
                item={course}
                showingType="courses"
                className="category-card course-card-mini"
                showLockBadge={!course.access}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CoursesPage
